import { useEffect } from "react";
import { useTranslation } from "../../../hooks/useTranslation";
import { CALLBACKS, triggerCallback } from "../../../utils/commonUtils";
import { getShopperCitFailureMessage } from "../../../utils/paymentOutcome";
import NuveiCardForm from "./NuveiCardForm";
import { useCheckout } from "../../../context/CheckoutContext";
import { useIframeResize } from "../../../hooks/useIframeResize";

/**
 * AltruonNuveiCardForm
 *
 * Altruon wrapper around NuveiCardForm for the embedded-checkout (iframe) scenario.
 *
 * Responsibilities:
 *  - Resizes the parent iframe via postMessage as content grows.
 *  - Listens for "makePayment" postMessage from the parent window and
 *    forwards it into the CheckoutContext trigger.
 *  - Routes NuveiCardForm callbacks back to the parent window.
 */
const AltruonNuveiCardForm = ({
  formData,
  itemDetails,
  billingPlatformId,
  routingEntries,
  planId,
  platformItemDetails,
  checkoutPageConfDto,
  checkoutProcessDto,
  transparent,
  prefetchedGatewayConnection,
  tenantId,
}) => {
  const queryParams = new URLSearchParams(window.location.search);
  const sessionId = queryParams.get("session_id");

  const { t } = useTranslation();
  const { triggerPaymentEvent } = useCheckout();

  useIframeResize({ enabled: true, padding: 40 });

  // Forward "makePayment" postMessage from parent → CheckoutContext trigger
  useEffect(() => {
    const handleMessage = (event) => {
      if (event?.data?.type === "makePayment") {
        triggerPaymentEvent();
      }
    };
    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [triggerPaymentEvent]);

  const onParentCallback = (event) => {
    switch (event.type) {
      case "finalizeFormData":
        window.parent.postMessage(event, "*");
        break;

      case "actionRequired":
        triggerCallback(CALLBACKS.ACTION_REQUIRED, {
          actionType: "REDIRECT",
          redirectUrl: event.url,
          message: "Payment initiated, redirecting you to complete the process…",
        });
        break;

      case "success":
        triggerCallback(CALLBACKS.SUCCESS, {
          status: "success",
          transactionId: event.transactionId,
          message: "Payment successfully initiated.",
        });
        window.parent.postMessage(
          {
            type: "paymentSuccess",
            data: { transactionId: event.transactionId },
          },
          "*"
        );
        break;

      default:
        triggerCallback(CALLBACKS.FAILURE, {
          status: "failed",
          message: getShopperCitFailureMessage(t),
        });
    }
  };

  return (
    <NuveiCardForm
      formData={formData}
      itemDetails={itemDetails}
      billingPlatformId={billingPlatformId}
      routingEntries={routingEntries}
      planId={planId}
      platformItemDetails={platformItemDetails}
      checkoutPageConfDto={checkoutPageConfDto}
      checkoutProcessDto={checkoutProcessDto}
      transparent={transparent}
      prefetchedGatewayConnection={prefetchedGatewayConnection}
      tenantId={tenantId}
      sessionId={sessionId}
      isEmbeddedCheckout={true}
      onParentCallback={onParentCallback}
    />
  );
};

export default AltruonNuveiCardForm;
